<template>
  <div class="player-main" :class="'player-main--' + variant">
    <div class="player-box">
      <div :id="containerId" class="dms-player-container"></div>
    </div>
  </div>
</template>

<script>
/**
 * 与 ouchn-app/hybrid/html/dmsplayer/index.html + js/index.js 保持一致：
 * - DOM：player-main > player-box > #video_player
 * - DmsPlayer：playType=3 防盗链、appId + vid 播放
 */
let embedUid = 0;

export default {
  name: "DmsPlayerEmbed",
  props: {
    vid: {
      type: [String, Number],
      default: "",
    },
    /** 封面，对应 index.js 的 cover / courseCover */
    cover: {
      type: String,
      default: "",
    },
    /** 直接 URL 播放（playType=1），一般走 vid 即可 */
    url: {
      type: String,
      default: "",
    },
    variant: {
      type: String,
      default: "video",
    },
    playType: {
      type: Number,
      default: 3,
    },
    appId: {
      type: Number,
      default: 0,
    },
    autoplay: {
      type: Boolean,
      default: false,
    },
    showBar: {
      type: Boolean,
      default: true,
    },
    /** 是否隐藏全屏按钮（index.js fullScreen=none） */
    hideFullscreen: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    embedUid += 1;
    return {
      containerId: "robot_dms_player_" + embedUid,
      player: null,
    };
  },
  computed: {
    resolvedAppId() {
      const fromProp = Number(this.appId);
      if (Number.isFinite(fromProp) && fromProp > 0) return fromProp;
      const fromEnv = Number(process.env.VUE_APP_DMS_APP_ID);
      if (Number.isFinite(fromEnv) && fromEnv > 0) return fromEnv;
      return 18;
    },
    resolvedPlayType() {
      const n = Number(this.playType);
      return Number.isFinite(n) && n > 0 ? n : 3;
    },
  },
  watch: {
    vid() {
      this.rebuildPlayer();
    },
    cover() {
      this.rebuildPlayer();
    },
  },
  mounted() {
    this.initPlayer();
  },
  beforeDestroy() {
    this.destroyPlayer();
  },
  methods: {
    rebuildPlayer() {
      this.destroyPlayer();
      this.$nextTick(() => {
        this.initPlayer();
      });
    },
    initPlayer() {
      const vid = String(this.vid ?? "").trim();
      const directUrl = String(this.url ?? "").trim();
      if ((!vid && !directUrl) || typeof DmsPlayer === "undefined") return;

      const host = document.getElementById(this.containerId);
      if (host) host.innerHTML = "";

      const cover = String(this.cover ?? "").trim();
      const playType = directUrl && !vid ? 1 : this.resolvedPlayType;

      this.player = new DmsPlayer({
        containerId: this.containerId,
        cover: cover || undefined,
        url: directUrl || undefined,
        currentTime: 0,
        autoplay: this.autoplay,
        loop: false,
        showBar: this.showBar,
        autoHideBar: true,
        autoHideBarTime: 5000,
        playType,
        appId: this.resolvedAppId,
        vid: vid || undefined,
        playCallBackTime: 1000,
        playCallBack: (data) => {
          this.$emit("play-callback", data);
        },
        onPlay: (data) => {
          this.$emit("play", data);
        },
        onPause: (data) => {
          this.$emit("pause", data);
        },
        onEnd: (data) => {
          this.$emit("ended", data);
        },
        onError: (data) => {
          this.$emit("error", data);
        },
        fullscreenCallBack: (value) => {
          this.$emit("fullscreen", value);
        },
      });

      if (this.hideFullscreen) {
        this.hideFullscreenButton();
      }
    },
    hideFullscreenButton() {
      window.setTimeout(() => {
        const host = document.getElementById(this.containerId);
        if (!host) return;
        const btn = host.querySelector(".dp-btn-fullscreen");
        if (btn) btn.style.display = "none";
      }, 500);
    },
    destroyPlayer() {
      if (this.player && typeof this.player.destroy === "function") {
        try {
          this.player.destroy();
        } catch (e) {
          // ignore
        }
      }
      this.player = null;
      const host = document.getElementById(this.containerId);
      if (host) host.innerHTML = "";
    },
  },
};
</script>

<style scoped>
/* 与 ouchn-app/hybrid/html/dmsplayer/css/index.css 一致 */
.player-main {
  max-width: 100%;
  margin: 8px auto;
  overflow: hidden;
  border-radius: 8px;
  background: #000;
}

.player-main .player-box {
  position: relative;
  width: 100%;
  padding-bottom: 56.25%;
}

.player-main .dms-player-container {
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
}

.player-main--audio .player-box {
  padding-bottom: 42%;
  min-height: 120px;
}

.player-main >>> .dp-controls .dp-right-controls .dp-btn-speed,
.player-main ::v-deep .dp-controls .dp-right-controls .dp-btn-speed {
  display: inline-block !important;
  height: 26px;
}
</style>
